import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Option,
  Radio,
  RadioGroup,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkFilled,
  ClipboardTaskListLtr20Regular,
  Dismiss24Regular,
  Person20Regular,
} from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';

import { ToastContext } from '@shared/contexts';
import { ButtonLoading, DateLabel } from '@shared/components';

import { TARGET_FUNCTION_OPTIONS, TRAINING_METHOD_OPTIONS } from '../constants';
import { getDefaultDueDate } from '../helpers';
import useResolvedTraining from '../hooks/useResolvedTraining';
import useTrainingStore from '../stores/useTrainingStore';
import { TTrainingEmployee } from '../types';
import AffectedEmployeesDialog from './AffectedEmployeesDialog';

const useStyles = makeStyles({
  surface: {
    maxWidth: '560px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  employeesRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  employeesCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  employeesIcon: {
    color: tokens.colorNeutralForeground3,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalXS,
  },
});

const uniqueOptions = (base: string[], current: string[]): string[] => Array.from(new Set([...base, ...current]));

type TProps = {
  documentId: string;
  documentName: string;
  versionId: string;
};

// The "Assign training" modal shown after the configured document is approved. All
// fields are prefilled from the training config but remain editable, and the
// affected employees appear to have been auto-identified from the target functions.
// Clicking "Assign training" records the assignment and shows a success toast.
const AssignTrainingDialog = ({ documentId, documentName, versionId }: TProps) => {
  const classes = useStyles();
  const { show } = useContext(ToastContext);
  const training = useResolvedTraining();

  const isAssignOpen = useTrainingStore((state) => state.isAssignOpen);
  const closeAssign = useTrainingStore((state) => state.closeAssign);
  const assign = useTrainingStore((state) => state.assign);

  const [trainingRequired, setTrainingRequired] = useState('Yes');
  const [targetFunctions, setTargetFunctions] = useState<string[]>([]);
  const [method, setMethod] = useState('');
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [employees, setEmployees] = useState<TTrainingEmployee[]>([]);
  const [isEmployeesOpen, setIsEmployeesOpen] = useState(false);

  // Prefill from config each time the dialog opens (it stays mounted).
  useEffect(() => {
    if (isAssignOpen && training) {
      setTrainingRequired('Yes');
      setTargetFunctions(training.targetFunctions);
      setMethod(training.trainingMethod);
      setDueDate(getDefaultDueDate(training.dueInDays));
      setEmployees(training.employees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill on open only; training is stable per session
  }, [isAssignOpen]);

  const methodOptions = useMemo(() => uniqueOptions(TRAINING_METHOD_OPTIONS, method ? [method] : []), [method]);
  const functionOptions = useMemo(() => uniqueOptions(TARGET_FUNCTION_OPTIONS, targetFunctions), [targetFunctions]);

  if (!training) return null;

  const isRequired = trainingRequired === 'Yes';
  const includedCount = employees.filter((employee) => employee.included).length;

  const handleAssign = () => {
    const count = includedCount;
    assign(documentId, { count, dueDate: dueDate || getDefaultDueDate(training.dueInDays) });
    show('success', `Training assigned to ${count} employee${count === 1 ? '' : 's'}.`);
  };

  return (
    <>
      <Dialog
        open={isAssignOpen}
        onOpenChange={(_, data) => {
          if (!data.open) closeAssign();
        }}
      >
        <DialogSurface className={classes.surface}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              <div className={classes.titleRow}>
                <ClipboardTaskListLtr20Regular className={classes.titleIcon} />
                <span>Assign training</span>
              </div>
            </DialogTitle>
            <DialogContent className={classes.content}>
              <MessageBar intent="info">
                <MessageBarBody>
                  {documentName}, Revision {versionId} was approved. The training requirement below was identified
                  automatically.
                </MessageBarBody>
              </MessageBar>

              <Field label="Training required">
                <RadioGroup
                  layout="horizontal"
                  value={trainingRequired}
                  onChange={(_, data) => setTrainingRequired(data.value)}
                >
                  <Radio value="Yes" label="Yes" />
                  <Radio value="No" label="No" />
                </RadioGroup>
              </Field>

              <Field label="Target functions">
                <Dropdown
                  multiselect
                  disabled={!isRequired}
                  placeholder="Select functions"
                  selectedOptions={targetFunctions}
                  value={targetFunctions.join(', ')}
                  onOptionSelect={(_, data) => setTargetFunctions(data.selectedOptions)}
                >
                  {functionOptions.map((option) => (
                    <Option key={option} value={option} text={option}>
                      {option}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <Field label="Training method">
                <Dropdown
                  disabled={!isRequired}
                  placeholder="Select a method"
                  selectedOptions={[method]}
                  value={method}
                  onOptionSelect={(_, data) => setMethod(data.optionValue ?? method)}
                >
                  {methodOptions.map((option) => (
                    <Option key={option} value={option} text={option}>
                      {option}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              <Field label="Due date">
                <DatePicker
                  disabled={!isRequired}
                  value={dueDate ? new Date(dueDate) : null}
                  formatDate={(date) => DateLabel({ children: date?.toISOString() || '', short: true })}
                  onSelectDate={(date) => setDueDate(date ? date.toISOString() : null)}
                  placeholder="Select a date"
                  firstDayOfWeek={1}
                />
              </Field>

              <Field label="Affected employees">
                <div className={classes.employeesRow}>
                  <Body1 className={classes.employeesCount}>
                    <Person20Regular className={classes.employeesIcon} />
                    {includedCount} employee{includedCount === 1 ? '' : 's'}
                  </Body1>
                  <Button appearance="secondary" disabled={!isRequired} onClick={() => setIsEmployeesOpen(true)}>
                    View / edit
                  </Button>
                </div>
                <Caption1 className={classes.hint}>Automatically identified from the target functions.</Caption1>
              </Field>
            </DialogContent>
            <DialogActions>
              <ButtonLoading
                appearance="primary"
                icon={<CheckmarkFilled />}
                disabled={!isRequired || includedCount === 0}
                onClick={handleAssign}
              >
                Assign training
              </ButtonLoading>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <AffectedEmployeesDialog
        isOpen={isEmployeesOpen}
        setIsOpen={setIsEmployeesOpen}
        employees={employees}
        onApply={setEmployees}
      />
    </>
  );
};

export default AssignTrainingDialog;
